import { motion } from "framer-motion";

const steps = [
  {
    n: "01",
    title: "Identity verification",
    body: "Before anything moves forward, I verify that the account details you provide — display name, registered email, phone number, date of birth — match your real identity and a valid government ID. This protects both sides and is non-negotiable.",
  },
  {
    n: "02",
    title: "Case assessment",
    body: "Once identity is confirmed, I review the platform's action against the account: reason for disable/restriction, prior history, and applicable policy. You get an honest read on whether the case is realistically resolvable.",
  },
  {
    n: "03",
    title: "Approach & timeline",
    body: "You receive a clear plan — the exact route (appeal, escalation, or platform-rep channel), an estimated timeline, expected cost, and any actions required from your side before we proceed.",
  },
  {
    n: "04",
    title: "Execution & follow-through",
    body: "Coordinated handling until the account is fully restored. You get status updates at every meaningful checkpoint — no vague waiting, no ghosting.",
  },
];

export const Process = () => {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="border-t border-border bg-card/40"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-36">
        <div className="mb-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-3xl">
            <span className="font-code text-xs uppercase tracking-[0.2em] text-accent">
              [ 02 ] — How I work
            </span>
            <h2 className="font-display font-bold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mt-5">
              A structured, verification-first process.
            </h2>
            <p className="font-body text-muted-foreground mt-6 max-w-xl leading-relaxed">
              Every case follows the same disciplined workflow. Nothing skipped,
              no shortcuts — because that&apos;s how legitimate recoveries actually
              happen.
            </p>
          </div>
        </div>

        <div className="flex flex-col">
          {steps.map((s) => (
            <motion.div
              key={s.n}
              data-testid={`process-step-${s.n}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-baseline border-t border-border py-10 sm:py-14 hover:pl-2 transition-[padding] duration-300"
            >
              <div className="md:col-span-4">
                <span className="font-code font-bold text-6xl sm:text-8xl text-muted-foreground/30 group-hover:text-accent transition-colors duration-300">
                  {s.n}
                </span>
              </div>
              <h3 className="md:col-span-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                {s.title}
              </h3>
              <p className="md:col-span-4 font-body text-muted-foreground leading-relaxed">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Business accounts note */}
        <motion.div
          data-testid="business-accounts-note"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 border border-border bg-background p-6 sm:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-start"
        >
          <div className="md:col-span-3">
            <span className="font-code text-[11px] uppercase tracking-[0.2em] text-accent">
              Business & verified accounts
            </span>
          </div>
          <div className="md:col-span-9">
            <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mb-3">
              A different, more involved process.
            </h3>
            <p className="font-body text-sm sm:text-base text-muted-foreground leading-relaxed max-w-3xl">
              Business Manager, verified (blue-tick), advertising, and creator
              accounts follow a separate workflow with additional verification
              (business documents, tax records, prior admin access, and
              linked-page ownership). Timelines, costs, and required
              documentation differ significantly — please mention it&apos;s a
              business or verified account in your first message so I can send
              the correct intake checklist.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Process;
